import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Download, RefreshCw, Trash2, MonitorDown, FolderSearch, Database, FileScan, CheckCircle2, AlertTriangle, Upload, ShieldCheck, Zap, Clock, ListChecks, Play } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Card } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Switch } from "../../components/ui/switch";
import { Input } from "../../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmtDt = (iso) => (iso ? new Date(iso).toLocaleString("it-IT") : "—");
const fmtKb = (kb) => (kb >= 1024 ? `${(kb / 1024).toFixed(1)} MB` : `${(kb ?? 0).toFixed(0)} KB`);

const downloadPs1 = async (endpoint, filename, setLoading) => {
  if (setLoading) setLoading(true);
  try {
    const r = await axios.get(`${API}/${endpoint}`, { params: { base_url: process.env.REACT_APP_BACKEND_URL }, responseType: "blob" });
    const url = URL.createObjectURL(r.data);
    const a = document.createElement("a");
    a.href = url; a.download = filename; a.click();
    URL.revokeObjectURL(url);
    toast.success(`${filename} scaricato!`);
  } catch (e) {
    toast.error(e.response?.data?.detail || "Errore nel download");
  } finally { if (setLoading) setLoading(false); }
};

const SyncSettingsCard = () => {
  const [data, setData] = useState(null);
  const [form, setForm] = useState({ sync_enabled: false, ddt_tipi: [], sync_window_days: 90, interval_min: 10 });
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dl, setDl] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/etifood-agent/sync-settings`);
      setData(r.data);
      if (!dirty) setForm({
        sync_enabled: r.data.sync_enabled, ddt_tipi: r.data.ddt_tipi || [],
        sync_window_days: r.data.sync_window_days, interval_min: r.data.interval_min,
      });
    } catch { /* ignore */ }
  }, [dirty]);

  useEffect(() => { load(); const t = setInterval(load, 15000); return () => clearInterval(t); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/etifood-agent/sync-settings`, {
        sync_enabled: form.sync_enabled, ddt_tipi: form.ddt_tipi,
        sync_from_date: null, sync_window_days: Number(form.sync_window_days) || 90,
        interval_min: Number(form.interval_min) || 10,
      });
      toast.success("Impostazioni salvate");
      setDirty(false);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
    finally { setSaving(false); }
  };

  const toggleTipo = (tipo) => {
    setDirty(true);
    setForm((f) => ({ ...f, ddt_tipi: f.ddt_tipi.includes(tipo) ? f.ddt_tipi.filter((t) => t !== tipo) : [...f.ddt_tipi, tipo] }));
  };

  const tipi = data?.tipi_available || [];

  return (
    <Card className="p-6 space-y-5 border-emerald-300" data-testid="sync-settings-card">
      <div className="flex items-center gap-2">
        <Zap className="w-5 h-5 text-emerald-600" />
        <h2 className="font-bold text-stone-800">Fase 2 — Sincronizzazione automatica DDT</h2>
      </div>
      <p className="text-sm text-stone-600">Un piccolo agente resta in esecuzione sul server di Etiflor (ALBANOEBEJA) e ogni pochi minuti invia qui i DDT nuovi o modificati. Sola lettura: non tocca Etiflor.</p>

      <div className="bg-stone-50 border border-stone-200 rounded-lg p-4 space-y-2">
        <p className="text-sm font-semibold text-stone-700">1. Installa l'agente sul server (una volta sola)</p>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" size="sm" onClick={() => downloadPs1("etifood-agent/sync-agent", "SyncEtiflor.ps1", setDl)} disabled={dl} data-testid="download-sync-agent-btn">
            <Download className="w-4 h-4 mr-1.5" /> Scarica agente (SyncEtiflor.ps1)
          </Button>
          <Button variant="outline" size="sm" onClick={() => downloadPs1("etifood-agent/sync-installer", "AvvioAutomaticoEtiflor.ps1", setDl)} disabled={dl} data-testid="download-sync-installer-btn">
            <Download className="w-4 h-4 mr-1.5" /> Avvio automatico (opzionale)
          </Button>
        </div>
        <p className="text-xs text-stone-500">Metti i due file nella stessa cartella sul server. Avvia <code className="bg-white px-1 rounded">SyncEtiflor.ps1</code> (tasto destro → Esegui con PowerShell) e lascialo in esecuzione. "Avvio automatico" lo fa ripartire da solo ad ogni accesso a Windows.</p>
      </div>

      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-semibold text-stone-700">2. Attiva la sincronizzazione</p>
            <p className="text-xs text-stone-500">Quando attiva, l'agente importa i DDT dei tipi selezionati.</p>
          </div>
          <Switch checked={form.sync_enabled} onCheckedChange={(v) => { setDirty(true); setForm((f) => ({ ...f, sync_enabled: v })); }} data-testid="sync-enabled-switch" />
        </div>

        <div>
          <p className="text-sm font-semibold text-stone-700 flex items-center gap-1.5 mb-2"><ListChecks className="w-4 h-4" /> Quali tipi documento sono DDT?</p>
          {tipi.length === 0 ? (
            <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2">Avvia l'agente almeno una volta: comparirà qui l'elenco dei tipi documento di Etiflor, così potrai spuntare quali sono i DDT.</p>
          ) : (
            <div className="grid sm:grid-cols-2 gap-2">
              {tipi.map((t, i) => {
                const key = t.tipo;
                const checked = form.ddt_tipi.includes(key);
                return (
                  <label key={i} className={`flex items-center gap-2 border rounded-lg px-3 py-2 cursor-pointer text-sm ${checked ? "border-emerald-400 bg-emerald-50" : "border-stone-200"}`} data-testid={`tipo-option-${key}`}>
                    <input type="checkbox" checked={checked} onChange={() => toggleTipo(key)} className="accent-emerald-600" />
                    <span className="font-mono font-semibold">{t.tipo}{t.serie ? `/${t.serie}` : ""}</span>
                    <span className="text-stone-500 text-xs">· {t.count} doc · {t.dal}→{t.al}</span>
                  </label>
                );
              })}
            </div>
          )}
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs text-stone-500 flex items-center gap-1"><Clock className="w-3 h-3" /> Ogni quanti minuti</label>
            <Input type="number" min="1" value={form.interval_min} onChange={(e) => { setDirty(true); setForm((f) => ({ ...f, interval_min: e.target.value })); }} className="h-9" data-testid="interval-input" />
          </div>
          <div>
            <label className="text-xs text-stone-500">Giorni da sincronizzare (finestra)</label>
            <Input type="number" min="1" value={form.sync_window_days} onChange={(e) => { setDirty(true); setForm((f) => ({ ...f, sync_window_days: e.target.value })); }} className="h-9" data-testid="window-input" />
          </div>
        </div>

        {dirty && (
          <Button onClick={save} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="save-sync-settings-btn">
            <CheckCircle2 className="w-4 h-4 mr-2" /> {saving ? "Salvataggio..." : "Salva impostazioni"}
          </Button>
        )}
      </div>

      <div className="border-t border-stone-200 pt-4">
        <div className="flex items-center justify-between mb-2">
          <p className="text-sm font-semibold text-stone-700">Stato sincronizzazione</p>
          <Badge className={data?.sync_enabled ? "bg-emerald-100 text-emerald-800 border border-emerald-200" : "bg-stone-100 text-stone-600 border border-stone-200"}>
            {data?.sync_enabled ? "Attiva" : "In pausa"}
          </Badge>
        </div>
        <div className="grid sm:grid-cols-3 gap-3 text-sm">
          <div><span className="text-stone-500 text-xs block">Ultima sync</span>{fmtDt(data?.last_run)}</div>
          <div><span className="text-stone-500 text-xs block">DDT importati totali</span><span className="font-semibold text-lg">{data?.total_ddt ?? 0}</span></div>
          <div><span className="text-stone-500 text-xs block">Ultimo batch</span>{data?.last_stats ? `${data.last_stats.inserted} nuovi, ${data.last_stats.updated} agg.` : "—"}</div>
        </div>
        {(data?.runs || []).length > 0 && (
          <details className="mt-2">
            <summary className="text-xs text-stone-500 cursor-pointer">Storico ultime sincronizzazioni</summary>
            <div className="mt-1 text-[11px] font-mono space-y-0.5">
              {data.runs.map((r, i) => <div key={i}>{fmtDt(r.at)} — ricevuti {r.received}, nuovi {r.inserted}, agg. {r.updated}, saltati {r.skipped}</div>)}
            </div>
          </details>
        )}
      </div>
    </Card>
  );
};


const FileTable = ({ files, emptyMsg }) => {
  if (!files || files.length === 0) return <p className="text-sm text-stone-400 italic py-2">{emptyMsg}</p>;
  return (
    <div className="overflow-x-auto border border-stone-200 rounded-lg max-h-72 overflow-y-auto">
      <table className="w-full text-xs">
        <thead className="bg-stone-50 sticky top-0">
          <tr>
            <th className="text-left px-3 py-2 font-semibold text-stone-600">Percorso</th>
            <th className="text-right px-3 py-2 font-semibold text-stone-600">Dimensione</th>
            <th className="text-right px-3 py-2 font-semibold text-stone-600">Modificato</th>
          </tr>
        </thead>
        <tbody>
          {files.map((f, i) => (
            <tr key={i} className="border-t border-stone-100">
              <td className="px-3 py-1.5 font-mono text-[11px] break-all">{f.path}</td>
              <td className="px-3 py-1.5 text-right whitespace-nowrap">{fmtKb(f.size_kb)}</td>
              <td className="px-3 py-1.5 text-right whitespace-nowrap text-stone-500">{f.modified}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const ReportDetailDialog = ({ reportId, onClose }) => {
  const [data, setData] = useState(null);
  useEffect(() => {
    axios.get(`${API}/etifood-agent/reports/${reportId}`).then((r) => setData(r.data)).catch(() => toast.error("Errore nel caricamento del rapporto"));
  }, [reportId]);

  const rep = data?.report || {};
  const mon = rep.monitor || {};

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto" data-testid="etifood-report-detail">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileScan className="w-5 h-5 text-[#8A1C2A]" />
            Rapporto diagnostica — {data?.computer_name || "..."}
          </DialogTitle>
        </DialogHeader>
        {!data ? <p className="text-sm text-stone-500 py-6">Caricamento...</p> : (
          <div className="space-y-5">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
              <div><span className="text-stone-500 text-xs block">PC</span><span className="font-semibold">{rep.computer_name}</span></div>
              <div><span className="text-stone-500 text-xs block">Utente</span><span className="font-semibold">{rep.username}</span></div>
              <div className="col-span-2"><span className="text-stone-500 text-xs block">Sistema</span><span className="font-semibold">{rep.os || (rep.kind === "db_probe" ? "Analisi Database" : "")}</span></div>
            </div>

            {rep.kind === "db_probe" && (
              <div className="space-y-5" data-testid="dbprobe-detail">
                <div className="text-sm"><span className="text-stone-500 text-xs block">Cartella programma Etiflor</span><span className="font-mono">{rep.program_dir || "—"}</span></div>

                {(rep.server_candidates?.length > 0 || rep.credentials_found?.length > 0) && (
                  <div className="grid sm:grid-cols-3 gap-3 text-sm bg-stone-50 border border-stone-200 rounded-lg p-3">
                    <div><span className="text-stone-500 text-xs block">Server SQL</span>{(rep.server_candidates || []).map((s, i) => <div key={i} className="font-mono text-xs">{s}</div>)}</div>
                    <div><span className="text-stone-500 text-xs block">Database</span>{(rep.database_names || rep.databases || []).map((s, i) => <div key={i} className="font-mono text-xs">{s}</div>)}</div>
                    <div><span className="text-stone-500 text-xs block">Credenziali SQL</span>{(rep.credentials_found || []).map((s, i) => <div key={i} className="font-mono text-xs">{s.user} / {s.pass}</div>)}</div>
                  </div>
                )}

                {rep.data_preview && (
                  <section>
                    <h3 className="font-bold text-sm text-[#8A1C2A] mb-2">📄 Anteprima dati vendite / DDT</h3>
                    {(rep.data_preview.venditat_tipi || []).length > 0 && (
                      <div className="mb-3">
                        <p className="text-xs font-semibold text-stone-600 mb-1">Tipi di documento in VENDITAT (VTTIPO / serie):</p>
                        <div className="overflow-x-auto border border-stone-200 rounded-lg">
                          <table className="w-full text-xs">
                            <thead className="bg-stone-50"><tr>{Object.keys(rep.data_preview.venditat_tipi[0]).map((k) => <th key={k} className="text-left px-3 py-1.5">{k}</th>)}</tr></thead>
                            <tbody>{rep.data_preview.venditat_tipi.map((row, i) => <tr key={i} className="border-t border-stone-100">{Object.values(row).map((v, j) => <td key={j} className="px-3 py-1.5 font-mono">{v}</td>)}</tr>)}</tbody>
                          </table>
                        </div>
                      </div>
                    )}
                    {[["tipidoc", "TIPIDOC (descrizioni tipi documento)"], ["venditat_recent", "Ultimi 3 documenti (testata)"], ["ddt_1726_header", "DDT 1726 — testata"], ["ddt_1726_lines", "DDT 1726 — righe"]].map(([key, label]) => (
                      (rep.data_preview[key] || []).length > 0 && (
                        <details key={key} className="mb-2 border border-stone-200 rounded-lg">
                          <summary className="px-3 py-2 text-xs font-semibold cursor-pointer hover:bg-stone-50">{label} ({rep.data_preview[key].length})</summary>
                          <pre className="text-[10px] bg-stone-900 text-stone-100 rounded-b p-2 overflow-x-auto max-h-72 whitespace-pre-wrap break-all">{JSON.stringify(rep.data_preview[key], null, 2)}</pre>
                        </details>
                      )
                    ))}
                  </section>
                )}

                {(rep.databases || []).length > 0 && (
                  <section>
                    <h3 className="font-bold text-sm text-emerald-700 mb-2 flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4" /> Database SQL Server trovati</h3>
                    <div className="flex flex-wrap gap-1.5">{rep.databases.map((db, i) => <Badge key={i} className="bg-emerald-100 text-emerald-800 border border-emerald-200">{db}</Badge>)}</div>
                  </section>
                )}

                {(rep.ddt_search || []).length > 0 && (
                  <section>
                    <h3 className="font-bold text-sm text-[#8A1C2A] mb-2">🎯 DDT 1726 trovato nel database</h3>
                    {rep.ddt_search.map((d, i) => (
                      <div key={i} className="mb-2 border border-stone-200 rounded-lg p-3">
                        <p className="text-xs font-semibold">Tabella: <code className="bg-stone-100 px-1 rounded">{d.table}</code> (colonna numero: {d.num_column}, {d.matches} righe)</p>
                        <pre className="text-[10px] bg-stone-900 text-stone-100 rounded p-2 mt-1 overflow-x-auto max-h-48">{JSON.stringify(d.sample, null, 2)}</pre>
                      </div>
                    ))}
                  </section>
                )}

                <section>
                  <h3 className="font-bold text-sm text-stone-800 mb-2">Stringhe di connessione trovate ({(rep.conn_strings || []).length})</h3>
                  {(rep.conn_strings || []).length === 0 ? <p className="text-sm text-stone-400 italic">Nessuna — vedi il contenuto dei file di configurazione qui sotto</p> : (
                    <ul className="text-[11px] font-mono space-y-1">{rep.conn_strings.map((c, i) => <li key={i} className="bg-stone-50 border border-stone-200 rounded px-2 py-1 break-all"><span className="text-stone-400">{c.file}:</span> {c.line}</li>)}</ul>
                  )}
                </section>

                {(rep.tables || []).length > 0 && (
                  <section>
                    <h3 className="font-bold text-sm text-stone-800 mb-2 flex items-center gap-1.5"><Database className="w-4 h-4 text-[#8A1C2A]" /> Tabelle del database ({rep.tables.length})</h3>
                    <div className="overflow-x-auto border border-stone-200 rounded-lg max-h-96 overflow-y-auto">
                      <table className="w-full text-xs">
                        <thead className="bg-stone-50 sticky top-0"><tr><th className="text-left px-3 py-2">Tabella</th><th className="text-right px-3 py-2">Righe</th><th className="text-left px-3 py-2">Colonne (solo tabelle chiave)</th></tr></thead>
                        <tbody>
                          {rep.tables.filter(t => t.interesting).concat(rep.tables.filter(t => !t.interesting)).map((t, i) => (
                            <tr key={i} className={`border-t border-stone-100 ${t.interesting ? "bg-amber-50/50" : ""}`}>
                              <td className="px-3 py-1.5 font-mono">{t.interesting && "⭐ "}{t.table}</td>
                              <td className="px-3 py-1.5 text-right">{t.rows}</td>
                              <td className="px-3 py-1.5 text-[10px] text-stone-500">{(t.columns || []).join(", ")}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </section>
                )}

                <section>
                  <h3 className="font-bold text-sm text-stone-800 mb-2">Contenuto file di configurazione ({(rep.config_files || []).length})</h3>
                  {(rep.config_files || []).map((f, i) => (
                    <details key={i} className="mb-2 border border-stone-200 rounded-lg">
                      <summary className="px-3 py-2 text-xs font-mono cursor-pointer hover:bg-stone-50">{f.has_conn_hint && "🔑 "}{f.name} <span className="text-stone-400">({f.size_kb} KB)</span></summary>
                      <pre className="text-[10px] bg-stone-900 text-stone-100 rounded-b p-2 overflow-x-auto max-h-64 whitespace-pre-wrap break-all">{f.content}</pre>
                    </details>
                  ))}
                </section>

                {(rep.sql_servers || []).some(s => !s.ok) && (
                  <details className="border border-stone-200 rounded-lg">
                    <summary className="px-3 py-2 text-xs cursor-pointer text-stone-500">Tentativi di connessione SQL (debug)</summary>
                    <ul className="text-[10px] font-mono p-2 space-y-1">{rep.sql_servers.map((s, i) => <li key={i} className={s.ok ? "text-emerald-600" : "text-stone-400"}>{s.ok ? "✓" : "✗"} {s.conn} {s.error ? `— ${s.error}` : ""}</li>)}</ul>
                  </details>
                )}
              </div>
            )}

            {rep.kind !== "db_probe" && rep.monitor && (
              <section>
                <h3 className="font-bold text-sm text-emerald-700 mb-2 flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4" /> Test guidato — file toccati da Etifood (LA PARTE PIÙ IMPORTANTE)</h3>
                <p className="text-xs font-semibold text-stone-600 mb-1">File NUOVI creati durante il test ({(mon.files_created || []).length}):</p>
                <FileTable files={mon.files_created} emptyMsg="Nessun file nuovo rilevato" />
                <p className="text-xs font-semibold text-stone-600 mb-1 mt-3">File MODIFICATI durante il test ({(mon.files_changed || []).length}):</p>
                <FileTable files={mon.files_changed} emptyMsg="Nessun file modificato rilevato" />
              </section>
            )}
            {rep.kind !== "db_probe" && !rep.monitor && (
              <div className="flex items-center gap-2 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                <AlertTriangle className="w-4 h-4 shrink-0" /> Il test guidato è stato saltato: senza questo passaggio è più difficile individuare il database. Rilancia la diagnostica se possibile.
              </div>
            )}

            {rep.kind !== "db_probe" && (<>
            <section>
              <h3 className="font-bold text-sm text-stone-800 mb-2 flex items-center gap-1.5"><FolderSearch className="w-4 h-4 text-[#8A1C2A]" /> Cartelle Etifood trovate ({(rep.etifood_dirs || []).length})</h3>
              {(rep.etifood_dirs || []).length === 0 ? <p className="text-sm text-stone-400 italic">Nessuna cartella con nome Etifood trovata</p> : (
                <ul className="text-xs font-mono space-y-1">{rep.etifood_dirs.map((d, i) => <li key={i} className="bg-stone-50 border border-stone-200 rounded px-2 py-1 break-all">{d}</li>)}</ul>
              )}
            </section>

            <section>
              <h3 className="font-bold text-sm text-stone-800 mb-2 flex items-center gap-1.5"><Database className="w-4 h-4 text-[#8A1C2A]" /> File database candidati ({(rep.db_candidates || []).length})</h3>
              <FileTable files={rep.db_candidates} emptyMsg="Nessun file database trovato" />
            </section>

            {(rep.ext_summary || []).length > 0 && (
              <section>
                <h3 className="font-bold text-sm text-stone-800 mb-2">Tipi di file nelle cartelle Etifood</h3>
                <div className="flex flex-wrap gap-1.5">
                  {rep.ext_summary.slice(0, 30).map((e, i) => (
                    <Badge key={i} variant="outline" className="text-[11px]">{e.ext || "(senza ext)"} × {e.count} — {fmtKb(e.total_kb)}</Badge>
                  ))}
                </div>
              </section>
            )}

            <div className="grid sm:grid-cols-2 gap-4">
              <section>
                <h3 className="font-bold text-sm text-stone-800 mb-2">Driver ODBC installati</h3>
                <div className="flex flex-wrap gap-1.5">
                  {(rep.odbc_drivers || []).map((d, i) => <Badge key={i} className="bg-stone-100 text-stone-700 text-[11px] border border-stone-200">{d}</Badge>)}
                  {(rep.odbc_drivers || []).length === 0 && <p className="text-sm text-stone-400 italic">Nessuno rilevato</p>}
                </div>
              </section>
              <section>
                <h3 className="font-bold text-sm text-stone-800 mb-2">Provider OLEDB</h3>
                <div className="flex flex-wrap gap-1.5">
                  {(rep.oledb_providers || []).map((d, i) => <Badge key={i} className="bg-stone-100 text-stone-700 text-[11px] border border-stone-200">{d}</Badge>)}
                  {(rep.oledb_providers || []).length === 0 && <p className="text-sm text-stone-400 italic">Nessuno rilevato</p>}
                </div>
              </section>
            </div>

            {(rep.installs || []).length > 0 && (
              <section>
                <h3 className="font-bold text-sm text-stone-800 mb-2">Programmi installati (match Etifood)</h3>
                <ul className="text-xs space-y-1">{rep.installs.map((p, i) => <li key={i} className="bg-stone-50 border border-stone-200 rounded px-2 py-1">{p.name} {p.version} — {p.publisher} {p.install_location && <span className="font-mono text-stone-500">({p.install_location})</span>}</li>)}</ul>
              </section>
            )}
            </>)}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default function EtifoodAgent() {
  const [config, setConfig] = useState(null);
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(false);
  const [detailId, setDetailId] = useState(null);

  const load = useCallback(async () => {
    try {
      const [c, r] = await Promise.all([
        axios.get(`${API}/etifood-agent/config`),
        axios.get(`${API}/etifood-agent/reports`),
      ]);
      setConfig(c.data);
      setReports(r.data || []);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 20000);
    return () => clearInterval(t);
  }, [load]);

  const downloadLauncher = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/etifood-agent/launcher`, {
        params: { base_url: process.env.REACT_APP_BACKEND_URL },
        responseType: "blob",
      });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "DiagnosticaEtifood.ps1";
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Script scaricato! Portalo sul PC di Etifood ed eseguilo (tasto destro → Esegui con PowerShell).");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore nel download");
    } finally { setLoading(false); }
  };

  const downloadDbProbe = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/etifood-agent/db-probe`, {
        params: { base_url: process.env.REACT_APP_BACKEND_URL },
        responseType: "blob",
      });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "AnalisiDatabaseEtiflor.ps1";
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Passo 2 scaricato! Eseguilo sul PC di Etifood (tasto destro → Esegui con PowerShell).");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore nel download");
    } finally { setLoading(false); }
  };

  const uploadReport = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    try {
      const text = await file.text();
      const json = JSON.parse(text);
      await axios.post(`${API}/etifood-agent/report-upload`, json);
      toast.success("Rapporto caricato con successo!");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "File non valido: seleziona il file rapporto-etifood-....json salvato sul Desktop");
    }
  };

  const deleteReport = async (id) => {
    if (!window.confirm("Eliminare questo rapporto?")) return;
    try {
      await axios.delete(`${API}/etifood-agent/reports/${id}`);
      toast.success("Rapporto eliminato");
      load();
    } catch { toast.error("Errore"); }
  };

  return (
    <div className="space-y-6" data-testid="etifood-agent-page">
      <div>
        <h1 className="text-2xl font-bold text-stone-900 flex items-center gap-2">
          <MonitorDown className="w-6 h-6 text-[#8A1C2A]" /> Sync Etifood (PC)
        </h1>
        <p className="text-sm text-stone-500 mt-1">Fase 1 — Diagnostica: scopriamo dove Etifood salva i dati per poi sincronizzarli automaticamente con l'app.</p>
      </div>

      <Card className="p-6 space-y-4">
        <h2 className="font-bold text-stone-800">Come funziona (3 passi)</h2>
        <ol className="space-y-3 text-sm text-stone-700">
          <li className="flex gap-3">
            <span className="w-6 h-6 rounded-full bg-[#8A1C2A] text-white flex items-center justify-center text-xs font-bold shrink-0">1</span>
            <span><strong>Scarica lo script qui sotto</strong> (<code className="bg-stone-100 px-1 rounded">DiagnosticaEtifood.ps1</code>) e portalo sul PC dove è installato Etifood (via chiavetta USB, email o WhatsApp Web).</span>
          </li>
          <li className="flex gap-3">
            <span className="w-6 h-6 rounded-full bg-[#8A1C2A] text-white flex items-center justify-center text-xs font-bold shrink-0">2</span>
            <span><strong>Tasto destro sul file → "Esegui con PowerShell"</strong>. Si apre una finestra: segui le istruzioni in italiano (ti chiederà di creare un DDT di prova su Etifood mentre lo script osserva). Legge soltanto, non modifica nulla — puoi anche aprirlo con il Blocco Note per controllare.</span>
          </li>
          <li className="flex gap-3">
            <span className="w-6 h-6 rounded-full bg-[#8A1C2A] text-white flex items-center justify-center text-xs font-bold shrink-0">3</span>
            <span><strong>Il rapporto arriva automaticamente qui sotto.</strong> Se l'invio automatico viene bloccato, lo script salva una copia sul Desktop (<code className="bg-stone-100 px-1 rounded">rapporto-etifood-….json</code>): caricala col pulsante "Carica rapporto".</span>
          </li>
        </ol>

        <div className="flex items-start gap-2 text-xs text-sky-800 bg-sky-50 border border-sky-200 rounded-lg px-3 py-2.5" data-testid="av-note">
          <ShieldCheck className="w-4 h-4 shrink-0 mt-0.5" />
          <div>
            <p className="font-semibold">L'antivirus segnala il file? È un falso positivo, nessun virus.</p>
            <p className="mt-1">Lo script legge solo informazioni (dove Etifood salva i dati) e le invia all'app: non installa e non modifica nulla. Puoi aprirlo con il Blocco Note e leggerlo. Se Windows/Kaspersky lo blocca:</p>
            <ul className="list-disc ml-4 mt-1 space-y-0.5">
              <li>Tasto destro sul file → <strong>Proprietà</strong> → spunta <strong>"Annulla blocco / Sblocca"</strong> → OK, poi riprova.</li>
              <li>In alternativa apri PowerShell e incolla: <code className="bg-white/70 px-1 rounded break-all">powershell -ExecutionPolicy Bypass -File "$env:USERPROFILE\Downloads\DiagnosticaEtifood.ps1"</code></li>
              <li>Se serve, aggiungi il file alle <strong>esclusioni</strong> dell'antivirus (è la cartella Download).</li>
            </ul>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-3 pt-1">
          <Button onClick={downloadLauncher} disabled={loading} className="bg-[#8A1C2A] hover:bg-[#6d1621]" data-testid="download-launcher-btn">
            <Download className="w-4 h-4 mr-2" /> {loading ? "Preparazione..." : "Scarica DiagnosticaEtifood.ps1"}
          </Button>
          <label>
            <input type="file" accept=".json,application/json" className="hidden" onChange={uploadReport} data-testid="upload-report-input" />
            <span className="inline-flex items-center gap-2 h-10 px-4 rounded-md border border-stone-300 text-sm font-medium text-stone-700 hover:bg-stone-50 cursor-pointer" data-testid="upload-report-btn">
              <Upload className="w-4 h-4" /> Carica rapporto (dal Desktop)
            </span>
          </label>
        </div>
        {config?.last_report_at && (
          <p className="text-xs text-emerald-700 flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5" /> Ultimo rapporto ricevuto: {fmtDt(config.last_report_at)}</p>
        )}
      </Card>

      <Card className="p-6 space-y-4 border-[#C5A059]/40">
        <div className="flex items-center gap-2">
          <Database className="w-5 h-5 text-[#C5A059]" />
          <h2 className="font-bold text-stone-800">Passo 2 — Analisi Database (dopo il Passo 1)</h2>
        </div>
        <p className="text-sm text-stone-600">Una volta trovata la cartella di Etiflor, questo secondo script legge la configurazione e <strong>mappa il database SQL Server</strong> (elenco tabelle di DDT, clienti, articoli…) sempre in <strong>sola lettura</strong>. È il passaggio che permette di costruire il sync automatico completo.</p>
        <div className="flex flex-wrap items-center gap-3">
          <Button onClick={downloadDbProbe} disabled={loading} variant="outline" className="border-[#C5A059] text-[#8A1C2A] hover:bg-[#C5A059]/10" data-testid="download-dbprobe-btn">
            <Download className="w-4 h-4 mr-2" /> Scarica AnalisiDatabaseEtiflor.ps1
          </Button>
          <span className="text-xs text-stone-400">Eseguilo come il Passo 1 (tasto destro → Esegui con PowerShell)</span>
        </div>
      </Card>

      <SyncSettingsCard />

      <Card className="p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-bold text-stone-800">Rapporti ricevuti ({reports.length})</h2>
          <Button variant="outline" size="sm" onClick={load} data-testid="refresh-reports-btn">
            <RefreshCw className="w-4 h-4 mr-1.5" /> Aggiorna
          </Button>
        </div>
        {reports.length === 0 ? (
          <p className="text-sm text-stone-400 italic py-4 text-center" data-testid="no-reports-msg">Nessun rapporto ancora ricevuto. La pagina si aggiorna da sola ogni 20 secondi.</p>
        ) : (
          <div className="space-y-2">
            {reports.map((r) => (
              <div key={r.id} className="flex items-center justify-between gap-3 border border-stone-200 rounded-lg px-4 py-3 hover:bg-stone-50 cursor-pointer" onClick={() => setDetailId(r.id)} data-testid={`report-row-${r.id}`}>
                <div className="min-w-0">
                  <p className="font-semibold text-sm text-stone-800">{r.computer_name || "PC sconosciuto"} <span className="text-stone-400 font-normal">— {r.username}</span></p>
                  <p className="text-xs text-stone-500">{fmtDt(r.received_at)} · {r.os}</p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Badge variant="outline" className="text-[11px]">Cartelle: {r.summary?.etifood_dirs ?? 0}</Badge>
                  <Badge variant="outline" className="text-[11px]">DB: {r.summary?.db_candidates ?? 0}</Badge>
                  {(r.summary?.monitor_created ?? 0) + (r.summary?.monitor_changed ?? 0) > 0 ? (
                    <Badge className="bg-emerald-100 text-emerald-800 border border-emerald-200 text-[11px]">Test: {(r.summary.monitor_created || 0) + (r.summary.monitor_changed || 0)} file</Badge>
                  ) : (
                    <Badge className="bg-amber-100 text-amber-800 border border-amber-200 text-[11px]">Test saltato</Badge>
                  )}
                  <Button variant="ghost" size="icon" className="text-stone-400 hover:text-red-600" onClick={(e) => { e.stopPropagation(); deleteReport(r.id); }} data-testid={`delete-report-${r.id}`}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {detailId && <ReportDetailDialog reportId={detailId} onClose={() => setDetailId(null)} />}
    </div>
  );
}
